"use client";

import Image from "next/image";
import { renderRichText } from "@storyblok/react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { EventTag } from "@/components/layout/EventTag";
import { MemberLockIcon } from "@/components/icons/MemberLockIcon";
import { RichText } from "@/components/typography/RichText";
import { formatEventDateRange } from "@/lib/utils/date";

export type EventPreviewData = {
  title: string;
  date: Date;
  endDate?: Date;
  eventType: string;
  image?: { src: string; alt: string };
  memberOnly?: boolean;
  description?: any;
  experts?: { name: string; image?: { filename: string; alt?: string } }[];
};

type Props = {
  event: EventPreviewData | null;
  onClose: () => void;
};

export function EventPreviewModal({ event, onClose }: Props) {
  if (!event) return null;

  const formattedDate = event.endDate
    ? formatEventDateRange(event.date, event.endDate)
    : formatEventDateRange(event.date, event.date);

  const descriptionHtml =
    event.description && typeof event.description === "object"
      ? renderRichText(event.description)
      : null;

  // Trips (content type `travel`) map to "reisen" via eventTypeFromContent.
  // The info sessions about them ("reiseInfo") are ordinary online events.
  const isTrip = event.eventType === "reisen";

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg p-0 block max-h-[85vh] overflow-y-auto overscroll-contain rounded-lg bg-sand-10 border-0 gap-0" closeClassName="rounded-full bg-sand-10 p-1.5 text-black opacity-100 hover:bg-sand-25 shadow-sm">
        {event.image && (
          <div className="relative w-full aspect-[16/9] overflow-hidden">
            <Image
              src={event.image.src}
              alt={event.image.alt}
              fill
              className="object-cover"
            />
            {/* Same pairing as the event cards: category badge with the
                member lock next to it. */}
            <div className="absolute top-4 left-4 flex items-center [&>*+*]:ml-2">
              <EventTag variant={event.eventType} memberOnly={event.memberOnly} />
              {event.memberOnly && <MemberLockIcon locked />}
            </div>
          </div>
        )}

        <div className="p-6 space-y-3">
          <DialogTitle className="text-headline-md">
            {event.title}
          </DialogTitle>

          <p className="text-copy-sm text-black-80">{formattedDate}</p>

          {/*
           * Every event format is an online session — DiaTalks, TopicTalks,
           * Sprechstunden and Community all run over Zoom. Trips are the
           * exception and are physical by definition.
           *
           * This used to read a Storyblok `online` boolean, but the field was
           * unmaintained: 11 of 51 events had it set to false while carrying a
           * Zoom link in the same record, so the modal told visitors
           * "Vor-Ort-Event" about an online DiaTalk. Deriving it from the type
           * is both correct and nothing anyone has to keep up to date.
           *
           * If an in-person event format is ever added, this needs a real
           * field again — with a clear meaning, not a revived `online`.
           */}
          {!isTrip && (
            <p className="text-copy-sm text-gray-75">Online-Event</p>
          )}

          {descriptionHtml && (
            <RichText html={descriptionHtml} className="text-copy" />
          )}

          {event.experts && event.experts.length > 0 && (
            <div className="flex [&>*+*]:ml-3 pt-2">
              {event.experts.map((expert) => (
                <div key={expert.name} className="flex items-center [&>*+*]:ml-2">
                  {expert.image?.filename && (
                    <Image
                      src={expert.image.filename}
                      alt={expert.image.alt || expert.name}
                      width={32}
                      height={32}
                      className="rounded-full object-cover object-top"
                    />
                  )}
                  <span className="text-copy-sm text-black-80">
                    {expert.name}
                  </span>
                </div>
              ))}
            </div>
          )}

        </div>
      </DialogContent>
    </Dialog>
  );
}
